import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { clearAuth, getUser } from "@/lib/api";
import Logo from "@/components/Logo";
import BackupButton from "@/components/BackupButton";
import { LayoutDashboard, BookOpen, Users, LogOut, Eye, TrendingUp, ListChecks, MessageCircleHeart } from "lucide-react";

const items = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true, testid: "nav-dashboard" },
  { to: "/admin/courses", label: "Courses", icon: BookOpen, testid: "nav-courses" },
  { to: "/admin/learners", label: "Learners", icon: Users, testid: "nav-learners" },
  { to: "/admin/progress", label: "Progress", icon: TrendingUp, testid: "nav-progress" },
  { to: "/admin/feedback", label: "Feedback", icon: MessageCircleHeart, testid: "nav-feedback" },
  { to: "/learn", label: "Learner Preview", icon: Eye, testid: "nav-preview" },
  { to: "/admin/quizzes", label: "Quiz Library", icon: ListChecks, testid: "nav-quizzes" },
];

export default function AdminLayout({ children }) {
  const loc = useLocation();
  const nav = useNavigate();
  const user = getUser();

  const isActive = (item) => item.exact ? loc.pathname === item.to : loc.pathname.startsWith(item.to);

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="w-64 bg-white border-r border-slate-200 flex flex-col shrink-0">
        <div className="px-5 py-5 border-b border-slate-200">
          <Logo />
        </div>
        <nav className="flex-1 px-3 py-5 space-y-0.5">
          {items.map((item) => {
            const Icon = item.icon;
            const active = isActive(item);
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  active
                    ? "bg-blue-50 text-blue-900 font-medium"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`}
              >
                <Icon size={17} strokeWidth={1.7} />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="border-t border-slate-200 p-4">
          <div className="text-xs text-slate-500 mb-2">Signed in as</div>
          <div className="text-sm font-medium text-slate-900">{user?.name}</div>
          <div className="text-xs text-slate-500 mb-3">{user?.email}</div>
          <button
            data-testid="logout-button"
            onClick={() => { clearAuth(); nav("/login"); }}
            className="flex items-center gap-2 text-xs text-slate-600 hover:text-red-600 transition-colors"
          >
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top admin chrome — backup button stays visible across all admin pages */}
        <header className="bg-white border-b border-slate-200 px-6 py-2.5 flex items-center justify-end shrink-0">
          <BackupButton />
        </header>
        <main className="flex-1 overflow-x-hidden">{children}</main>
      </div>
    </div>
  );
}
